import { Box, Tab, Tabs } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useUserStore } from "store";
import UnsavedChangesDialog from "../shared/UnsavedChangesDialog";
import { can } from "../../utils/access";
import { CustomTabPanel, a11yProps } from "./tabPanelUtils";
import PermissionMatrixSection from "./PermissionMatrixSection";
import TeamMembersTab from "./TeamMembersTab";

// Top-level "Käyttöoikeudet" (Permissions) settings tab, laid out like Ominaisuudet:
// "Tiimin jäsenet" (members and invitations) and, for admins, "Roolien käyttöoikeudet"
// (the per-organization permission matrix). Only the matrix reports dirty state; it is
// bubbled to MainSettingsView so the tab-switch and route-leave guards keep working.
function OrganizationPermissionsTab({ t, readOnly = false, onDirtyChange }) {
  const { currentOrganization } = useUserStore();
  const [subValue, setSubValue] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const subTabDirtyRef = useRef(false);
  const subSaveFnRef = useRef(null);
  const [pendingSubTab, setPendingSubTab] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subSwitchSaving, setSubSwitchSaving] = useState(false);

  const canEditRoles = can(currentOrganization, "team.roles") && !readOnly;

  const handleSubDirtyChange = useCallback(
    (dirty, saveFn) => {
      subTabDirtyRef.current = dirty;
      subSaveFnRef.current = saveFn;
      onDirtyChange?.(dirty, saveFn);
    },
    [onDirtyChange]
  );

  const doSwitchSubTab = useCallback(
    (newValue) => {
      subTabDirtyRef.current = false;
      subSaveFnRef.current = null;
      onDirtyChange?.(false, null);
      setSubValue(newValue);
    },
    [onDirtyChange]
  );

  const subTabs = [
    {
      key: "team",
      label: t("Team Members"),
      render: () => <TeamMembersTab readOnly={readOnly} onShowRoles={canEditRoles ? () => doSwitchSubTab(1) : undefined} />,
    },
  ];
  if (canEditRoles) {
    subTabs.push({
      key: "roles",
      label: t("permissions.editor.title"),
      render: (handleDirty) => <PermissionMatrixSection onDirtyChange={handleDirty} />,
    });
  }

  useEffect(() => {
    if (subValue >= subTabs.length) {
      setSubValue(0);
    }
  }, [subTabs.length, subValue]);

  // Deep link support: /settings?tab=permissions&subtab=roles selects a sub-tab once and
  // then drops the param so a later manual sub-tab switch is not fought by this effect.
  const subTabKeys = subTabs.map((tab) => tab.key).join(",");
  useEffect(() => {
    const requested = searchParams.get("subtab");
    if (!requested) return;
    const index = subTabKeys.split(",").indexOf(requested);
    if (index >= 0) {
      setSubValue(index);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("subtab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, subTabKeys]);

  const handleSubChange = (event, newValue) => {
    if (subTabDirtyRef.current) {
      setPendingSubTab(newValue);
      setShowSubDialog(true);
    } else {
      doSwitchSubTab(newValue);
    }
  };

  const handleSubSwitchSave = useCallback(async () => {
    setSubSwitchSaving(true);
    try {
      if (subSaveFnRef.current) {
        await subSaveFnRef.current();
      }
      setShowSubDialog(false);
      if (pendingSubTab !== null) {
        const tab = pendingSubTab;
        setPendingSubTab(null);
        setTimeout(() => doSwitchSubTab(tab), 0);
      }
    } catch (err) {
      // Save failed: stay on the current sub-tab
    } finally {
      setSubSwitchSaving(false);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchDiscard = useCallback(() => {
    setShowSubDialog(false);
    if (pendingSubTab !== null) {
      const tab = pendingSubTab;
      setPendingSubTab(null);
      setTimeout(() => doSwitchSubTab(tab), 0);
    }
  }, [pendingSubTab, doSwitchSubTab]);

  const handleSubSwitchCancel = useCallback(() => {
    setShowSubDialog(false);
    setPendingSubTab(null);
  }, []);

  const activeValue = subValue < subTabs.length ? subValue : 0;

  if (subTabs.length === 1) {
    return <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>{subTabs[0].render(handleSubDirtyChange)}</Box>;
  }

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeValue}
          onChange={handleSubChange}
          aria-label="permission settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              minWidth: { xs: "auto", sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: "13px", sm: "14px" },
            },
          }}
        >
          {subTabs.map((tab, index) => (
            <Tab key={tab.key} label={tab.label} data-testid={`permissions-subtab-${tab.key}`} {...a11yProps(index, "permissions")} />
          ))}
        </Tabs>
      </Box>

      {subTabs.map((tab, index) => (
        <CustomTabPanel key={tab.key} value={activeValue} index={index} idPrefix="permissions">
          {tab.render(handleSubDirtyChange)}
        </CustomTabPanel>
      ))}

      <UnsavedChangesDialog
        open={showSubDialog}
        onSave={handleSubSwitchSave}
        onDiscard={handleSubSwitchDiscard}
        onCancel={handleSubSwitchCancel}
        saving={subSwitchSaving}
      />
    </Box>
  );
}

export default withTranslation()(OrganizationPermissionsTab);
